import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

/**
 * Who owns (and pays for) a project (#1177).
 *
 * A user in several unrelated organizations had no unambiguous org, so their observations carried a
 * null institution_id and their FITS frames came out with empty SSORG/INSTABBR. The cure is to stop
 * inferring the owner and ask: options are Personal plus every organization the user belongs to —
 * child orgs included — each labelled with the subscription that would back it.
 *
 * Selection rules, matching the server's listEligibleProjectOwners:
 *   - exactly one selectable owner -> chosen silently, no decision to make;
 *   - more than one -> nothing is preselected on create. The old form defaulted to Personal, which
 *     is how ambiguous ownership got recorded without anyone choosing it;
 *   - an owner the server marked unselectable (no subscription, under
 *     SUBSCRIPTION_ENFORCEMENT=enforce) is shown disabled rather than hidden, so the reason is
 *     visible instead of the option silently missing.
 *
 * `backed` is advisory unless enforcement is on; the server decides, this only renders it.
 */
const ProjectOwnerSelect = ({
  owners = [],
  value,                 // { owner_type, owner_id } — owner_id null means Personal
  onChange,              // ({ owner_type, owner_id }) => void
  disabled = false,
  isEdit = false,
  loading = false,
  error = false,
}) => {
  // Personal is the user's own id server-side; the forms model it as owner_id null, so key options
  // by a synthetic value and translate on the way out.
  const optionKey = (o) => (o.ownerType === 'user' ? '__personal__' : o.ownerId);
  const currentKey = value?.owner_type === 'organization' && value?.owner_id
    ? String(value.owner_id)
    : (value?.owner_type === 'user' ? '__personal__' : '');

  const selectable = useMemo(() => owners.filter((o) => o.selectable), [owners]);

  // Exactly one real choice — take it rather than asking a question with one answer.
  useEffect(() => {
    if (isEdit || loading || currentKey || selectable.length !== 1) return;
    const only = selectable[0];
    onChange(only.ownerType === 'user'
      ? { owner_type: 'user', owner_id: null }
      : { owner_type: 'organization', owner_id: only.ownerId });
  }, [isEdit, loading, currentKey, selectable, onChange]);

  if (loading || owners.length === 0) return null;

  const handleChange = (e) => {
    const key = e.target.value;
    onChange(key === '__personal__'
      ? { owner_type: 'user', owner_id: null }
      : { owner_type: 'organization', owner_id: key });
  };

  const needsChoice = !isEdit && !currentKey && selectable.length > 1;

  return (
    <FormControl fullWidth error={error || needsChoice ? error : false}>
      <InputLabel>Owner</InputLabel>
      <Select
        value={currentKey}
        onChange={handleChange}
        label="Owner"
        disabled={disabled}
        renderValue={(key) => {
          const o = owners.find((opt) => optionKey(opt) === key);
          return o ? o.label : '';
        }}
      >
        {owners.map((o) => (
          <MenuItem key={optionKey(o)} value={optionKey(o)} disabled={!o.selectable}>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {o.label}
                  {/* Two different reasons an option is unusable, and the user needs to know which:
                      no authority to create projects there, or nothing paying for it. Authority is
                      reported whatever the enforcement mode — it is not a commercial rule. */}
                  {o.authorized === false ? (
                    <Chip label="no authority" size="small" variant="outlined" color="warning" />
                  ) : !o.backed ? (
                    <Chip
                      label="no subscription"
                      size="small"
                      variant="outlined"
                      color={o.selectable ? 'default' : 'warning'}
                    />
                  ) : null}
                </Box>
              }
              secondary={o.backed && o.backedViaId
                ? (owners.find((x) => x.ownerId === o.backedViaId)?.label
                  ? `covered by ${owners.find((x) => x.ownerId === o.backedViaId).label} subscription`
                  : 'covered by a parent organization subscription')
                : undefined}
            />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {isEdit
          ? 'Reassign this project to a different owner'
          : 'Who owns this project. This sets the organization stamped on its images.'}
      </FormHelperText>
      {needsChoice && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          You belong to more than one organization — choose which owns this project.
        </Typography>
      )}
    </FormControl>
  );
};

export default ProjectOwnerSelect;
